import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Grid, Chip, CircularProgress,
  Table, TableHead, TableBody, TableRow, TableCell, TableContainer,
} from '@mui/material';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import AssignmentIcon from '@mui/icons-material/Assignment';
import DashboardIcon from '@mui/icons-material/Dashboard';
import DownloadIcon from '@mui/icons-material/Download';
import LocationCityIcon from '@mui/icons-material/LocationCity';
import {
  StatCard, DashboardGrid, DashboardPanel, DashboardSection,
  SimpleTable, CollapsibleGroup,
} from '@NP-Webber/crm-kit';
import api from '../../services/api.js';
import { useCkInside } from '@NP-Webber/crm-kit/dist/next/index.js';
import RegistrationsDashboardNext from './RegistrationsDashboardNext.jsx';

export const SOURCE_LABELS = { ivr: 'ימות', web: 'ווב', import: 'ייבוא' };
export const EXAM_TYPE_LABELS = { time: 'זמן', shimur: 'שימור', miktzoa: 'מקצוע' };

/* ── ייצוא CSV סדור לפי עיר ────────────────────────────────────── */
export function exportDashboardCsv({ byKolel, byExam, byExamCity, summary, periodLabel }) {
  const esc = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
  const row = (...cells) => cells.map(esc).join(',');

  const lines = [];

  // כותרת
  lines.push(row(`דשבורד רישומים${periodLabel ? ` — ${periodLabel}` : ''}`, '', '', ''));
  lines.push(row(`סה"כ רישומים: ${summary.totalRegistrations ?? 0}`,
                 `נבחנים ייחודיים: ${summary.uniqueExaminees ?? 0}`,
                 `כוללים: ${summary.totalKolels ?? 0}`, ''));
  lines.push(row('', '', '', ''));

  // סחר 1: נבחנים לפי כולל מסודר לפי עיר
  lines.push(row('נבחנים לפי כולל', '', '', ''));
  lines.push(row('עיר', 'שם כולל', 'קוד כולל', 'נבחנים ייחודיים'));

  const sortedKolels = [...byKolel].sort((a, b) => {
    const cityA = (a.city || '').localeCompare(b.city || '', 'he');
    if (cityA !== 0) return cityA;
    return (a.kolelName || '').localeCompare(b.kolelName || '', 'he');
  });

  // סיכומי עיר
  let lastCity = null;
  const cityTotals = {};
  sortedKolels.forEach((k) => {
    const c = k.city || 'לא ידוע';
    cityTotals[c] = (cityTotals[c] || 0) + (k.uniqueMembers || 0);
  });

  sortedKolels.forEach((k) => {
    const city = k.city || 'לא ידוע';
    if (city !== lastCity) {
      if (lastCity !== null) {
        lines.push(row(`סה"כ ${lastCity}`, '', '', cityTotals[lastCity]));
        lines.push(row('', '', '', ''));
      }
      lastCity = city;
    }
    lines.push(row(city, k.kolelName || '', k.kolelCode ?? '', k.uniqueMembers ?? 0));
  });
  if (lastCity !== null) {
    lines.push(row(`סה"כ ${lastCity}`, '', '', cityTotals[lastCity]));
  }

  lines.push(row('', '', '', ''));

  // סחר 2: פי מבחן
  lines.push(row('רישומים לפי מבחן', '', '', ''));
  lines.push(row('קוד', 'שם מבחן', 'סוג', 'רישומים', 'נבחנים ייחודיים'));
  byExam.forEach((e) => {
    lines.push(row(e.code ?? '', e.name || '', EXAM_TYPE_LABELS[e.type] || e.type || '', e.count ?? 0, e.uniqueMembers ?? 0));
  });

  lines.push(row('', '', '', ''));

  // סחר 3: פי עיר בחינה
  lines.push(row('רשומים לפי עיר בחינה', '', '', '', ''));
  lines.push(row('עיר בחינה', 'קוד מבחן', 'שם מבחן', 'סוג', 'רשומים'));
  byExamCity.forEach((cityRow) => {
    const city = cityRow.location || 'ללא עיר';
    const sortedExams = [...(cityRow.exams || [])].sort((a, b) => (a.code ?? 0) - (b.code ?? 0));
    sortedExams.forEach((e) => {
      lines.push(row(city, e.code ?? '', e.name || '', EXAM_TYPE_LABELS[e.type] || e.type || '', e.count ?? 0));
    });
    lines.push(row(`סה"כ ${city}`, '', '', '', cityRow.total ?? 0));
    lines.push(row('', '', '', '', ''));
  });

  const BOM = '\uFEFF';
  const csv = BOM + lines.join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `דשבורד_רישומים${periodLabel ? `_${periodLabel}` : ''}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

/* ── רשימת עיר בחינה → מבחנים ───────────────────────────────────── */
function ExamCityBreakdown({ byExamCity, loading }) {
  return (
    <CollapsibleGroup
      items={byExamCity}
      getKey={(item) => item.location || 'ללא עיר'}
      getLabel={(item) => item.location || 'ללא עיר'}
      getBadge={(item) => `סה"כ: ${item.total}`}
      badgeColor="secondary"
      itemIcon={<LocationCityIcon />}
      iconColor="secondary"
      loading={loading}
      emptyMsg="אין נתונים"
      renderHeader={(item) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography fontWeight={700}>{item.location || 'ללא עיר'}</Typography>
          <Typography variant="body2" color="text.secondary">
            ({(item.exams || []).length} מבחנים)
          </Typography>
        </Box>
      )}
      renderContent={(item) => {
        const sortedExams = [...(item.exams || [])].sort((a, b) => (a.code ?? 0) - (b.code ?? 0));
        const cityKey = item.location || 'ללא עיר';
        return (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.50' }}>
                  <TableCell align="center" sx={{ fontWeight: 600 }}>קוד</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>שם מבחן</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 600 }}>סוג</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 600, color: 'secondary.main' }}>רשומים</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedExams.map((ex, i) => (
                  <TableRow key={i} hover>
                    <TableCell align="center">
                      {ex.code != null ? <Chip label={ex.code} size="small" variant="outlined" /> : '—'}
                    </TableCell>
                    <TableCell align="right">{ex.name || '—'}</TableCell>
                    <TableCell align="center">{EXAM_TYPE_LABELS[ex.type] || ex.type || '—'}</TableCell>
                    <TableCell align="center">
                      <Chip label={ex.count} color="secondary" size="small" sx={{ fontWeight: 700 }} />
                    </TableCell>
                  </TableRow>
                ))}
                <TableRow sx={{ bgcolor: 'secondary.50' }}>
                  <TableCell colSpan={3} align="right" sx={{ fontWeight: 700 }}>סה"כ {cityKey}</TableCell>
                  <TableCell align="center">
                    <Chip label={item.total} color="secondary" size="small" sx={{ fontWeight: 700 }} />
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        );
      }}
    />
  );
}

/* ── הדשבורד הראשי ───────────────────────────────────────────────────── */
/** In the new design the same props render RegistrationsDashboardNext. */
export default function RegistrationsDashboard(props) {
  const inside = useCkInside();
  return inside ? <RegistrationsDashboardNext {...props} /> : <RegistrationsDashboardLegacy {...props} />;
}

function RegistrationsDashboardLegacy({ periodId, periodLabel }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchDashboard = useCallback(async () => {
    setLoading(true);
    try {
      const params = periodId ? { periodId } : {};
      const { data: res } = await api.get('/reports/registrations-dashboard', { params });
      setData(res);
    } catch (err) {
      console.error('שגיאה בטעינת דשבורד הרשמות:', err);
    } finally {
      setLoading(false);
    }
  }, [periodId]);

  useEffect(() => { fetchDashboard(); }, [fetchDashboard]);

  const summary    = data?.summary    || {};
  const byExam     = data?.byExam     || [];
  const byKolel    = data?.byKolel    || [];
  const bySource   = data?.bySource   || [];
  const byExamCity = data?.byExamCity || [];

  const examColumns = [
    { key: 'code',         title: 'קוד',   align: 'center',
      render: (v) => v != null ? <Chip label={v} size="small" variant="outlined" /> : '—' },
    { key: 'name',         title: 'שם מבחן' },
    { key: 'type',         title: 'סוג',
      render: (v) => EXAM_TYPE_LABELS[v] || v || '—' },
    { key: 'count',        title: 'רישומים', align: 'center',
      render: (v) => <Chip label={v} color="primary" size="small" sx={{ fontWeight: 700 }} /> },
    { key: 'uniqueMembers', title: 'נבחנים ייחודיים', align: 'center',
      render: (v) => <Chip label={v} color="secondary" size="small" sx={{ fontWeight: 700 }} /> },
  ];

  const kolelColumns = [
    { key: 'city',         title: 'עיר' },
    { key: 'kolelName',    title: 'שם כולל' },
    { key: 'kolelCode',    title: 'קוד', align: 'center',
      render: (v) => v != null ? <Chip label={v} size="small" variant="outlined" /> : '—' },
    { key: 'uniqueMembers', title: 'נבחנים ייחודיים', align: 'center',
      render: (v) => <Chip label={v} color="success" size="small" sx={{ fontWeight: 700 }} /> },
  ];

  return (
    <DashboardPanel
      title="דשבורד רישומים"
      subtitle={periodLabel}
      icon={<DashboardIcon fontSize="small" />}
      color="primary.main"
      loading={loading}
      onRefresh={fetchDashboard}
      actions={[
        {
          icon: <DownloadIcon fontSize="small" />,
          tooltip: 'ייצוא ל-Excel (סדור לפי עיר)',
          disabled: !data || loading,
          onClick: () => exportDashboardCsv({ byKolel, byExam, byExamCity, summary, periodLabel }),
        },
      ]}
    >
      {/* ── קלפי סיכום ── */}
      <DashboardGrid columns={3} gap={2}>
        <StatCard
          loading={loading}
          icon={<PeopleAltIcon />}
          label="נבחנים ייחודיים"
          value={summary.uniqueExaminees}
          color="primary.main"
        />
        <StatCard
          loading={loading}
          icon={<AssignmentIcon />}
          label="סה״כ רישומים"
          value={summary.totalRegistrations}
          color="secondary.main"
        />
        <StatCard
          loading={loading}
          icon={<AccountBalanceIcon />}
          label="כוללים עם רישומים"
          value={summary.totalKolels}
          color="success.main"
        />
        {bySource.map((s) => (
          <StatCard
            key={s.source}
            loading={loading}
            icon={<AssignmentIcon fontSize="small" />}
            label={`מקור: ${SOURCE_LABELS[s.source] || s.source}`}
            value={s.count}
            color="info.main"
          />
        ))}
      </DashboardGrid>

      <Grid container spacing={2.5}>
        {/* רישומים לפי עיר בחינה — רוחב מלא */}
        <Grid item xs={12}>
          <DashboardSection
            title="רשומים לפי עיר בחינה"
            icon={<LocationCityIcon />}
            iconColor="secondary.main"
            count={byExamCity.length}
            countColor="secondary"
            headerBgColor="secondary.50"
            borderColor="secondary.200"
          >
            <ExamCityBreakdown byExamCity={byExamCity} loading={loading} />
          </DashboardSection>
        </Grid>

        {/* רישומים לפי מבחן | לפי כולל */}
        <Grid item xs={12} md={6}>
          <DashboardSection
            title="רישומים לפי מבחן"
            count={byExam.length}
            noPadding
          >
            <SimpleTable columns={examColumns} rows={byExam} emptyMsg="אין רישומים למבחנים" />
          </DashboardSection>
        </Grid>

        <Grid item xs={12} md={6}>
          <DashboardSection
            title="נבחנים לפי כולל"
            count={byKolel.length}
            noPadding
          >
            <SimpleTable columns={kolelColumns} rows={byKolel} emptyMsg="אין נתונים" />
          </DashboardSection>
        </Grid>
      </Grid>
    </DashboardPanel>
  );
}
